import React, { useEffect } from "react";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import InputGroup from "react-bootstrap/InputGroup";
import ToggleButtonGroup from "react-bootstrap/ToggleButtonGroup";
import ToggleButton from "react-bootstrap/ToggleButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUndo } from "@fortawesome/pro-regular-svg-icons";

import Panel from "../Panel";
import PanelSection from "../PanelSection";
import NumericFormControl from "../NumericFormControl";
import ColorPicker from "../ColorPicker";

import ChantDocument from "../../js/ChantDocument";
import FontPane from "./FontPane";
import { fontKeys } from "../../utils/fonts";
import { defaultColor, defaultRubricColor, colors } from "../../utils/colors";

const clearFontKeys = Object.fromEntries(fontKeys.map(key => [key, undefined]));

const allowedRanges = {
  in: {
    width: {
      min: 4,
      max: 11,
    },
    height: {
      min: 6,
      max: 17,
    }
  },
  cm: {
    width: {
      min: 10,
      max: 30,
    },
    height: {
      min: 14,
      max: 44,
    }
  }
}

function DocumentPanel(props) {
  const doc = props.document,
    handleChange = props.onChange,
    defaults = ChantDocument.getDefaultChantDocument(),
    baseStaffSize = ChantDocument.getStaffMultiplier(doc),
    staffSize = ChantDocument.getStaffSize(doc),
    staffSizePts = Number(staffSize.toFixed(1)),
    staffSizeMinPts = Math.round(baseStaffSize * 0.5),
    staffSizeMaxPts = Math.round(baseStaffSize * 1.5),
    textTypes = props.textTypes.map(key => ChantDocument.getTextType(doc, key)),
    handleStaffSizeChange = ChantDocument.getHandleStaffSizeChange(doc),
    handleFontChange = font => handleChange({ ...clearFontKeys, font }),
    ResetButtonForDocumentProperty = props => {
      let defaultValue =
          "default" in props ? props.default : defaults[props.docKey],
        defaultValues = { [props.docKey]: defaultValue };
      if (props.docKeys) {
        defaultValues = props.docKeys.reduce(
          (result, docKey) => ({
            ...result,
            [docKey]: defaultValue
          }),
          defaultValues
        );
      }
      return (
        (props.show || doc[props.docKey] !== defaultValue) && (
          <Button
            variant="circle"
            className="ml-auto zerowidth"
            onClick={e => {
              handleChange(defaultValues);
            }}
          >
            <FontAwesomeIcon icon={faUndo} />
          </Button>
        )
      );
    };
  useEffect(() => {
    for (let key of props.textTypes) {
      let docKey = `${key}Size`;
      if (!(docKey in props.document)) {
        handleChange({ [docKey]: 1 }); // all font sizes default to 1
      }
    }
  }, [props.textTypes, props.document, handleChange]);
  const units = doc.units || 'in';
  const allowedRange = allowedRanges[units];

  return (
    <Panel title="Document" className="border-left">
      <PanelSection title="Page">
      <label>
          <Panel.Label>Units</Panel.Label>
          <ToggleButtonGroup
            name="alignment"
            value={units}
            className="w-100"
            size="sm"
            onChange={units => handleChange({ units })}
          >
            <ToggleButton variant="outline-primary" value="in">
              Inches
            </ToggleButton>
            <ToggleButton variant="outline-primary" value="cm">
              Centimeters
            </ToggleButton>
          </ToggleButtonGroup>
        </label>
        <div className="d-flex mb-3">
          <label className="mb-0">
            <Panel.Label>Width</Panel.Label>
            <InputGroup>
              <NumericFormControl
                defaultValue={doc.width}
                required
                min={allowedRange.width.min}
                max={allowedRange.width.max}
                step="0.01"
                onChange={width => handleChange({ width })}
              />
              <InputGroup.Append>
                <InputGroup.Text>{units}</InputGroup.Text>
              </InputGroup.Append>
            </InputGroup>
          </label>
          <span className="mx-2 mb-1 align-self-end gray-300">&times;</span>
          <label className="mb-0">
            <Panel.Label>Height</Panel.Label>
            <InputGroup>
              <NumericFormControl
                min={allowedRange.height.min}
                max={allowedRange.height.max}
                step="0.01"
                defaultValue={doc.height}
                required
                onChange={height => handleChange({ height })}
              />
              <InputGroup.Append>
                <InputGroup.Text>{units}</InputGroup.Text>
              </InputGroup.Append>
            </InputGroup>
          </label>
        </div>
        <label className="mb-0">
          <Panel.Label>Margin</Panel.Label>
          <InputGroup style={{ width: "90px" }}>
            <NumericFormControl
              step="0.01"
              min="0"
              max={Math.min(doc.height, doc.width) / 2}
              defaultValue={doc.margin}
              required
              onChange={margin => handleChange({ margin })}
            />
            <InputGroup.Append>
              <InputGroup.Text>{units}</InputGroup.Text>
            </InputGroup.Append>
          </InputGroup>
        </label>
      </PanelSection>

      <PanelSection title="Text">
        <label>
          <Panel.Label>Alignment</Panel.Label>
          <ToggleButtonGroup
            name="alignment"
            value={doc.alignment}
            className="w-100"
            size="sm"
            onChange={alignment => handleChange({ alignment })}
          >
            <ToggleButton variant="outline-primary" value="english">
              English
            </ToggleButton>
            <ToggleButton variant="outline-primary" value="latin">
              Latin
            </ToggleButton>
          </ToggleButtonGroup>
        </label>
        <label>
          <Panel.Label className="mb-2">Drop Cap</Panel.Label>
          <Form.Switch
            id="switchDropCap"
            checked={doc.dropCap}
            label={doc.dropCap ? "On" : "Off"}
            onChange={e => handleChange({ dropCap: e.target.checked })}
          />
        </label>
        <label className="mb-3">
          <FontPane
            handleFontChange={handleFontChange}
            selectedValue={doc.font || ChantDocument.defaultFont}
          />
        </label>
        <label>
          <Panel.Label>
            <span className="form-inline d-flex">
              Staff Size:{" "}
              <span className="font-weight-bolder text-dark">
                <NumericFormControl
                  defaultValue={staffSizePts}
                  tabIndex="-1"
                  onChange={calculated =>
                    handleStaffSizeChange(calculated, handleChange)
                  }
                  plaintext
                  className="text-right p-0"
                  required
                  min={staffSizeMinPts}
                  max={staffSizeMaxPts}
                  step="0.1"
                  style={{
                    width: `${staffSizeMaxPts.toFixed(1).length}ch`
                  }}
                />
                pt
              </span>
              <ResetButtonForDocumentProperty
                docKey="staffSize"
                docKeys={textTypes.map(t => t.docKey)}
                show={textTypes.some(type => !type.isDefaultValue)}
              />
            </span>
          </Panel.Label>
          <input
            type="range"
            value={doc.staffSize}
            min={0.5}
            max={1.5}
            step={0.01}
            onChange={e =>
              handleChange({ staffSize: parseFloat(e.target.value) })
            }
            className="custom-range"
          />
        </label>
        {textTypes.map(type => (
          <label key={type.key}>
            <Panel.Label>
              <span className="form-inline d-flex">
                {type.display} Text Size:{" "}
                <span className="font-weight-bolder text-dark">
                  <NumericFormControl
                    defaultValue={type.calculatedFontSize}
                    tabIndex="-1"
                    onChange={calculated =>
                      type.handleTextSizeChange(calculated, handleChange)
                    }
                    plaintext
                    className="text-right p-0"
                    required
                    min={type.min}
                    max={type.max}
                    step="0.1"
                    style={{
                      width: `${type.max.toFixed(1).length}ch`
                    }}
                  />
                  pt
                </span>
                <ResetButtonForDocumentProperty
                  docKey={type.docKey}
                  default={1}
                />
              </span>
            </Panel.Label>
            <input
              type="range"
              value={doc[type.docKey] || 1}
              min={0.5}
              max={1.5}
              step={0.01}
              onChange={e =>
                handleChange({ [type.docKey]: parseFloat(e.target.value) })
              }
              className="custom-range range-tertiary"
            />
          </label>
        ))}
        <label>
          <Panel.Label className="d-flex">
            Horizontal Spacing
            <ResetButtonForDocumentProperty docKey="horizontalSpacing" />
          </Panel.Label>
          <input
            type="range"
            value={doc.horizontalSpacing}
            min={0.5}
            max={1.5}
            step={0.01}
            onChange={e =>
              handleChange({ horizontalSpacing: parseFloat(e.target.value) })
            }
            className="custom-range"
          />
        </label>
        <label className="mb-0">
          <Panel.Label className="d-flex">
            Vertical Spacing
            <ResetButtonForDocumentProperty docKey="verticalSpacing" />
          </Panel.Label>
          <input
            type="range"
            value={doc.verticalSpacing}
            min={0.5}
            max={2.5}
            step={0.01}
            onChange={e =>
              handleChange({ verticalSpacing: parseFloat(e.target.value) })
            }
            className="custom-range"
          />
        </label>
        <div className="mt-3 mb-2">
          <Panel.Label className="d-flex">
            Staff Color
            <ResetButtonForDocumentProperty docKey="staffColor" />
          </Panel.Label>
          <ColorPicker
            colors={colors}
            color={doc.staffColor || defaultColor}
            onChange={color => handleChange({ staffColor: color.hex })}
          />
        </div>
        <div className="mb-0">
          <Panel.Label className="d-flex">
            Rubric Color
            <ResetButtonForDocumentProperty docKey="rubricColor" />
          </Panel.Label>
          <ColorPicker
            colors={colors}
            color={doc.rubricColor || defaultRubricColor}
            onChange={color => handleChange({ rubricColor: color.hex })}
          />
        </div>
      </PanelSection>
    </Panel>
  );
}

export default DocumentPanel;
